/**
 * "Used by" — the shared fetch + cell for the tiered and parallel set pages.
 *
 * Both pages ask the same question of the same documents, so the queries live
 * here once: app settings names the active configs, then each config doc is
 * fetched and walked (see providerSetUsage.ts).
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { OperationsMap, ProviderTieredSet } from '../../types/settings';
import { activeConfigNames, buildSetUsage, type SetUsage } from './providerSetUsage';

/**
 * Usage maps for every provider set, plus whether the scan is still loading —
 * an empty map mid-flight would otherwise render as "not used" for a moment on
 * every row, which is the one answer an operator must not misread.
 */
export function useProviderSetUsage(tieredSets: ProviderTieredSet[]) {
    const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => adminApi.getSettings() });
    const names = activeConfigNames(settings);

    const configQueries = useQueries({
        queries: names.map(name => ({
            queryKey: ['pipeline-config', name],
            queryFn: () => adminApi.getPipelineConfig(name),
            // Config docs are large and change rarely; both pages share the cache.
            staleTime: 5 * 60 * 1000,
        })),
    });

    const loading = !settings || configQueries.some(q => q.isLoading);
    const configs: { name: string; operations?: OperationsMap }[] = [];
    configQueries.forEach((q, i) => {
        if (q.data) configs.push({ name: names[i], operations: q.data.operations });
    });

    const { tiered, parallel } = buildSetUsage(configs, tieredSets);
    return { tiered, parallel, loading, activeConfigNames: names };
}

export function UsedByCell({ usages, loading }: { usages?: SetUsage[]; loading: boolean }) {
    if (loading) return <span className="provider-sets-usage-empty">…</span>;
    if (!usages || usages.length === 0) {
        return <span className="provider-sets-usage-empty">Not used</span>;
    }
    return (
        <div className="provider-sets-usage">
            {usages.map(u => (
                <span
                    key={`${u.configName}:${u.operation}:${u.pipelineName}:${u.viaTieredSet ?? ''}`}
                    className={`provider-sets-usage-item${u.enabled ? '' : ' is-disabled'}`}
                    title={[
                        `config: ${u.configName}`,
                        u.viaTieredSet ? `via tiered set: ${u.viaTieredSet}` : null,
                        u.enabled ? null : 'pipeline is disabled',
                    ].filter(Boolean).join(' · ')}
                >
                    <span className="provider-sets-usage-op">{u.operation}</span>
                    <span className="provider-sets-usage-sep">·</span>
                    <span>{u.pipelineName}</span>
                    {u.viaTieredSet && <span className="provider-sets-usage-via">via {u.viaTieredSet}</span>}
                    {!u.enabled && <span className="provider-sets-usage-off">off</span>}
                </span>
            ))}
        </div>
    );
}
